/**
 * Chunk-load failure auto-recovery (021 fix-wave, user finding, live): after
 * a redeploy, a tab left open from before the deploy still holds the OLD
 * asset manifest — its next lazy route navigation requests a chunk file
 * (e.g. `/assets/Map-<oldhash>.js`) that no longer exists on the server,
 * and the dynamic `import()` rejects with "Failed to fetch dynamically
 * imported module." Platform-wide: wraps every `lazy()` loader in
 * `App.tsx`, not just SurfTime's.
 *
 * Recovery: the FIRST such failure reloads the page once — a fresh page
 * load fetches the CURRENT manifest, so the stale reference is simply
 * gone. A `sessionStorage` flag remembers that a reload was already
 * tried, so a genuinely broken deploy (the chunk is ALSO missing after
 * the reload) can't loop forever — a failure while the flag is set
 * re-throws, letting the nearest error boundary render its honest
 * "something went wrong, refresh" state instead of reloading again.
 *
 * The flag is CLEARED on the next successful chunk load (adversarial
 * review, 021 fix-wave): sessionStorage survives reloads, so a
 * never-cleared flag meant "ever failed this tab" — recovery worked
 * exactly once per tab lifetime, and a tab surviving TWO deploys hit the
 * error boundary on the second instead of auto-recovering. Clearing on
 * success restores the intended "reload once per failure episode"
 * semantics while still breaking genuine reload loops (a broken deploy
 * never reaches a successful load, so the flag stays set).
 */

export const CHUNK_RELOAD_FLAG = 'platform-chunk-reload-attempted';

export function withChunkReload<T>(loader: () => Promise<T>): () => Promise<T> {
  return () =>
    loader().then(
      (mod) => {
        window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
        return mod;
      },
      (error: unknown) => {
        const alreadyReloaded = window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
        if (alreadyReloaded) {
          throw error;
        }
        window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
        window.location.reload();
        // The reload is already in flight — never resolve/reject so nothing
        // downstream (Suspense, an error boundary) tries to render a
        // component that failed to load on THIS page instance.
        return new Promise<T>(() => {});
      },
    );
}
